import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { X, ChevronLeft, ChevronRight, Upload, Trash2, Loader2, Image as ImageIcon } from 'lucide-react';
import { toast } from 'react-toastify';
import { useSisConfig } from '../../contexts/SisConfigUi';
import SqlexecMasterDetail from '../sqlexec/SqlexecMasterDetail';
import SqlexecConfirmDialog from '../sqlexec/SqlexecConfirmDialog';
import { secureFetch } from '../../utils/authInterceptor';
import { compensarEscalaVh } from '../../utils/formHelpers';

// ==========================================
// CONSTANTES GLOBALES
// ==========================================
const ZOOM_LEVELS = [25, 50, 75, 100, 125, 150, 200, 300, 400];
const MIN_ZOOM = 25;
const MAX_ZOOM = 400;
const ZOOM_STEP = 25;
const FIT_ZOOM = 100;

/**
 * FileGalleryManager - Gestor universal de archivos
 * 
 * Sistema profesional de galería con:
 * - Zero-hardcode total (config desde BD)
 * - Sistema de zoom multinivel (25% - 400%)
 * - Navegación por teclado y mouse
 * - Botones dinámicos desde gallery_actions
 * - RBAC integrado
 * - Soporte múltiples formatos (imagen, PDF, otros)
 * 
 * @param {Object} config - Configuración desde schema.fileGallery
 * @param {number} parentValue - ID del registro padre
 * @param {Object} parentRow - Row completo del padre
 * @param {string} componentForm - Nombre del componente
 * @param {Object} schema - Schema completo
 * @param {Function} onClose - Callback al cerrar
 * @param {Function} onSuccess - Callback después de operación exitosa
 */
const FileGalleryManager = ({ 
  config,
  parentValue,
  parentRow,
  componentForm,
  schema,
  onClose,
  onSuccess
}) => {
  
  // ==========================================
  // CONTEXTO UI
  // ==========================================
  const { modales_ui, botones_ui } = useSisConfig();

  // ==========================================
  // REFS
  // ==========================================
  const fileInputRef = useRef(null);
  const imageContainerRef = useRef(null);

  // ==========================================
  // ESTADOS: Archivos y UI
  // ==========================================
  const [files, setFiles] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [galleryButtons, setGalleryButtons] = useState([]);

  // ==========================================
  // ESTADOS: Sistema de Zoom
  // ==========================================
  const [zoomLevel, setZoomLevel] = useState(FIT_ZOOM);
  const [viewMode, setViewMode] = useState('single');
  const [pendingDelete, setPendingDelete] = useState(false);

  // ==========================================
  // CONFIGURACIÓN: Schema
  // ==========================================
  const parentKey = config?.parentKey || '';
  const fileField = config?.fileField || 'archivo';
  const typeField = config?.typeField || 'tipo';
  const maxFiles = config?.maxFiles || 10;
  const maxFileSize = config?.maxFileSize || 5242880;
  const allowedTypes = useMemo(() => config?.allowedTypes || ['image/*'], [config?.allowedTypes]);
  const showThumbnails = config?.preview?.showThumbnails !== false;
  const showNavigation = config?.preview?.showNavigation !== false;
  const showCounter = config?.preview?.showCounter !== false;
  const title = schema?.title || 'Galería de Archivos';

  // ==========================================
  // CONFIGURACIÓN: UI desde sisconfig
  // ==========================================
  const modalConfig = useMemo(() => modales_ui || {}, [modales_ui]);
  const btnBase = useMemo(() => botones_ui || {}, [botones_ui]);
  const btnPrimary = useMemo(() => botones_ui?.primary || {}, [botones_ui?.primary]);
  const btnSecondary = useMemo(() => botones_ui?.secondary || {}, [botones_ui?.secondary]);
  const btnDanger = useMemo(() => botones_ui?.danger || {}, [botones_ui?.danger]);
  const btnSuccess   = useMemo(() => botones_ui?.success   || {}, [botones_ui?.success]);

  const containerConfig = useMemo(() => modales_ui?.container || {}, [modales_ui?.container]);
  const modalHeaderConfig = useMemo(() => modales_ui?.header || {}, [modales_ui?.header]);
  const closeButtonConfig = useMemo(() => modales_ui?.closeButton || {}, [modales_ui?.closeButton]);
  const footerConfig = useMemo(() => modales_ui?.footer || {}, [modales_ui?.footer]);

  const galleryUI = useMemo(() => modales_ui?.fileGallery || {}, [modales_ui?.fileGallery]);
  const controlsUI = useMemo(() => galleryUI?.controls || {}, [galleryUI?.controls]);
  const thumbnailsUI = useMemo(() => galleryUI?.thumbnails || {}, [galleryUI?.thumbnails]);

  // ==========================================
  // UTILIDAD: Permisos RBAC
  // ==========================================
  const getUserPermissionLevel = useCallback((componentFormParam) => {
    try {
      const sessionData = sessionStorage.getItem('mapasac_session');
      if (!sessionData) return 'read';
      
      const session = JSON.parse(sessionData);
      const permisos = session?.rbac?.permisos || [];
      
      let permisoEncontrado = null;
      
      // 1. Buscar por component_form exacto
      permisoEncontrado = permisos.find(p => p.componente_form === componentFormParam);
      
      // 2. Si no encuentra, buscar por código
      if (!permisoEncontrado) {
        const nombreLimpio = componentFormParam
          .toLowerCase()
          .replace('layout', '')
          .replace('panel_', '')
          .replace(/_/g, '');
        
        permisoEncontrado = permisos.find(p => {
          const codigoLimpio = (p.codigo || '').toLowerCase().replace(/_/g, '');
          return codigoLimpio.includes(nombreLimpio);
        });
      }

      // 3. Herencia de permisos del padre
      if (!permisoEncontrado && componentFormParam.includes('_')) {
        const parts = componentFormParam.split('_');
        let parentPrefix = '';
        
        for (let i = parts.length - 1; i >= 0; i--) {
          if (parts[i] && parts[i][0] === parts[i][0].toUpperCase()) {
            parentPrefix = parts.slice(0, i).join('_');
            break;
          }
        }

        if (parentPrefix) {
          permisoEncontrado = permisos.find(p => p.componente_form === parentPrefix);

          if (!permisoEncontrado) {
            const nombreLimpio = parentPrefix
              .toLowerCase()
              .replace('layout', '')
              .replace('panel_', '')
              .replace(/_/g, '');
            
            permisoEncontrado = permisos.find(p => {
              const codigoLimpio = (p.codigo || '').toLowerCase().replace(/_/g, '');
              return codigoLimpio.includes(nombreLimpio);
            });
          }
        }
      }
      
      // 4. Determinar nivel
      if (permisoEncontrado) {
        if (permisoEncontrado.puede_eliminar || permisoEncontrado.acciones === 'full') {
          return 'full';
        }
        if (permisoEncontrado.puede_actualizar || permisoEncontrado.acciones === 'edit') {
          return 'edit';
        }
        return 'read';
      }
    
      return 'read';
    } catch (error) {
      console.error('❌ Error obteniendo permisos:', error);
      return 'read';
    }
  }, []);

  const tienePermiso = useCallback((userLevel, requiredLevel) => {
    const jerarquia = { read: 1, edit: 2, full: 3 };
    return (jerarquia[userLevel] || 0) >= (jerarquia[requiredLevel] || 0);
  }, []);

  // ==========================================
  // UTILIDAD: Conversión de archivos
  // ==========================================
  const fileToBase64 = useCallback((file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }, []);

  // ==========================================
  // UTILIDAD: Hash SHA-256 (anti-duplicados)
  // ==========================================
  const calculateFileHash = useCallback(async (file) => {
    try {
      const buffer = await file.arrayBuffer();
      const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
      return hashHex;
    } catch (error) {
      console.error('Error calculando hash:', error);
      return null;
    }
  }, []);

  const checkDuplicate = useCallback((hash) => {
    if (!hash) return false;
    
    // Buscar hash en archivos ya cargados (estado local)
    return files.some(file => file.file_hash === hash);
  }, [files]);

  // ==========================================
  // EFFECT: Procesar botones dinámicos
  // ==========================================
  useEffect(() => {
    if (!config?.gallery_actions) {
      setGalleryButtons([]);
      return;
    }

    const userPerm = getUserPermissionLevel(componentForm);
    
    const botonesVisibles = Object.entries(config.gallery_actions)
      .filter(([key, btnConfig]) => {
        if (!btnConfig.active) return false;
        return tienePermiso(userPerm, btnConfig.permission_level);
      })
      .sort((a, b) => (a[1].order || 0) - (b[1].order || 0))
      .map(([key, btnConfig]) => ({ key, ...btnConfig }));
    
    setGalleryButtons(botonesVisibles);
  }, [config, componentForm, getUserPermissionLevel, tienePermiso]);

  // ==========================================
  // EFFECT: Cargar archivos al montar
  // ==========================================
  const loadFiles = useCallback(async () => {
    if (!config?.query || !parentValue) {
      toast.error('Configuración incompleta para FileGalleryManager');
      return;
    }

    setLoading(true);
    try {
      const token = sessionStorage.getItem('mapasac_access_token');
      const query = config.query.replace(':parentValue', '$1');
      const params = [parentValue];
      
      const response = await secureFetch('/api/sqlexec_generico/query-params', {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`, 
          'Content-Type': 'application/json' 
        },
        body: JSON.stringify({ query, params })
      });

      const json = await response.json();

      if (json.success && Array.isArray(json.data)) {
        setFiles(json.data);
        if (json.data.length > 0) {
          setCurrentIndex(0);
        }
      } else {
        setFiles([]);
      }
    } catch (error) {
      console.error('Error cargando archivos:', error);
      toast.error('Error al cargar archivos');
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [config, parentValue]);

  useEffect(() => {
    if (config?.query && parentKey && parentValue) {
      loadFiles();
    }
  }, [loadFiles, config, parentKey, parentValue]);

  // ==========================================
  // HANDLERS: Navegación
  // ==========================================
  const goToNext = useCallback(() => {
    if (files.length === 0) return;
    setCurrentIndex(prev => (prev + 1) % files.length);
  }, [files.length]);

  const goToPrev = useCallback(() => {
    if (files.length === 0) return;
    setCurrentIndex(prev => (prev - 1 + files.length) % files.length);
  }, [files.length]);

  const goToIndex = useCallback((index) => {
    setCurrentIndex(index);
  }, []);

  // ==========================================
  // HANDLERS: Zoom
  // ==========================================
  const handleZoomIn = useCallback(() => {
    setZoomLevel(prev => {
      const currentIdx = ZOOM_LEVELS.findIndex(level => level >= prev);
      if (currentIdx < ZOOM_LEVELS.length - 1) {
        return ZOOM_LEVELS[currentIdx + 1];
      }
      return Math.min(prev + ZOOM_STEP, MAX_ZOOM);
    });
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoomLevel(prev => {
      const currentIdx = ZOOM_LEVELS.findIndex(level => level >= prev);
      if (currentIdx > 0) {
        return ZOOM_LEVELS[currentIdx - 1];
      }
      return Math.max(prev - ZOOM_STEP, MIN_ZOOM);
    });
  }, []);
  
  const handleFitToScreen = useCallback(() => {
    setZoomLevel(FIT_ZOOM);
  }, []);

  const handleSetZoom = useCallback((level) => {
    setZoomLevel(level);
  }, []);

  /**
   * Toggle View Mode - Single ↔ Grid
   */
  const handleViewModeToggle = useCallback(() => {
    setViewMode(prev => prev === 'single' ? 'grid' : 'single');
  }, []);

  /**
   * Abrir archivo completo en nueva pestaña
   */
  const handleOpenFile = useCallback((file) => {
    if (!file) return;
    
    const fileData = file[fileField];
    const fileType = file[typeField] || 'application/octet-stream';
    
    if (!fileData) {
      toast.error('Archivo sin datos');
      return;
    }
    
    const dataUrl = `data:${fileType};base64,${fileData}`;
    
    // Abrir en nueva pestaña
    const newWindow = window.open();
    if (newWindow) {
      newWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Archivo - ${file.idinveimgs || 'Sin título'}</title>
            <style>
              body { margin: 0; padding: 0; overflow: hidden; }
              img { width: 100%; height: 100vh; object-fit: contain; }
              iframe { width: 100%; height: 100vh; border: none; }
            </style>
          </head>
          <body>
            ${fileType.startsWith('image/') 
              ? `<img src="${dataUrl}" alt="Imagen completa" />` 
              : `<iframe src="${dataUrl}"></iframe>`
            }
          </body>
        </html>
      `);
      newWindow.document.close();
    } else {
      toast.error('Permitir ventanas emergentes para abrir archivo');
    }
  }, [fileField, typeField]);

  // ==========================================
  // HANDLERS: Pan/Drag - ELIMINADO (simplificación para usuario final)
  // ==========================================

  // ==========================================
  // HANDLERS: Upload
  // ==========================================
  const handleUpload = useCallback(async (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length === 0) return;

    // Validar cantidad total
    if (files.length + selectedFiles.length > maxFiles) {
      toast.warning(`Máximo ${maxFiles} archivos. Quedan ${maxFiles - files.length} espacios.`);
      return;
    }

    // Subir todos los archivos
    setUploading(true);
    let successCount = 0;
    
    for (const file of selectedFiles) {
      // ✅ PASO 1: Calcular hash del archivo
      const fileHash = await calculateFileHash(file);
      
      // ✅ PASO 2: Verificar si ya existe
      const isDuplicate = checkDuplicate(fileHash);
      
      if (isDuplicate) {
        toast.warning(`${file.name}: Archivo duplicado (ya existe)`);
        continue;
      }

      // Validar tamaño
      if (file.size > maxFileSize) {
        const maxMB = (maxFileSize / 1048576).toFixed(1);
        toast.error(`${file.name}: Muy grande. Máximo ${maxMB}MB`);
        continue;
      }

      // Validar tipo
      const isAllowed = allowedTypes.some(type => {
        if (type.endsWith('/*')) {
          const category = type.split('/')[0];
          return file.type.startsWith(category + '/');
        }
        return file.type === type;
      });

      if (!isAllowed) {
        toast.error(`${file.name}: Tipo no permitido`);
        continue;
      }

      try {
        const token = sessionStorage.getItem('mapasac_access_token');
        const base64Data = await fileToBase64(file);

        const payload = {
          [parentKey]: parentValue,
          [fileField]: base64Data,
          [typeField]: file.type,
          file_hash: fileHash
        };

        const response = await secureFetch(`/api/sqlexec/${componentForm}/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        });

        const json = await response.json();

        if (json.success) {
          successCount++;
        } else {
          toast.error(`${file.name}: ${json.message || 'Error'}`);
        }
      } catch (error) {
        console.error(`Error subiendo ${file.name}:`, error);
        toast.error(`${file.name}: Error al subir`);
      }
    }

    setUploading(false);
    
    if (successCount > 0) {
      toast.success(`${successCount} archivo(s) subido(s) correctamente`);
      await loadFiles();
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [files.length, maxFiles, maxFileSize, allowedTypes, fileToBase64, calculateFileHash, checkDuplicate, parentKey, parentValue, fileField, typeField, componentForm, loadFiles]);

  // ==========================================
  // HANDLERS: Delete - Abrir modal confirmación
  // ==========================================
  const handleDeleteClick = useCallback(() => {
    if (files.length === 0) return;
    setPendingDelete(true);
  }, [files.length]);

  // ==========================================
  // HANDLERS: Delete - Ejecutar eliminación
  // ==========================================
  const handleConfirmDelete = useCallback(async () => {
    if (files.length === 0) return;

    const currentFile = files[currentIndex];
    const idField = config?.idField || Object.keys(currentFile)[0];
    const fileId = currentFile[idField];

    try {
      setDeleting(true);
      const token = sessionStorage.getItem('mapasac_access_token');

      const response = await secureFetch(`/api/sqlexec/${componentForm}/delete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ [idField]: fileId })
      });

      const json = await response.json();

      if (json.success) {
        toast.success('Archivo eliminado');
        
        if (currentIndex >= files.length - 1 && currentIndex > 0) {
          setCurrentIndex(currentIndex - 1);
        }
        
        await loadFiles();
        setPendingDelete(null);
      } else {
        toast.error(json.message || 'Error al eliminar');
      }
    } catch (error) {
      console.error('Error eliminando archivo:', error);
      toast.error('Error al eliminar archivo');
    } finally {
      setDeleting(false);
    }
  }, [files, currentIndex, config, componentForm, loadFiles]);

  // ==========================================
  // EFFECT: Resetear zoom al cambiar archivo
  // ==========================================
  useEffect(() => {
    setZoomLevel(FIT_ZOOM);
  }, [currentIndex]);

  // ==========================================
  // EFFECT: Zoom con Mouse Wheel (Ctrl + Scroll)
  // ==========================================
  useEffect(() => {
    const handleWheel = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        
        if (e.deltaY < 0) {
          handleZoomIn();
        } else {
          handleZoomOut();
        }
      }
    };

    const container = imageContainerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
      return () => container.removeEventListener('wheel', handleWheel);
    }
  }, [handleZoomIn, handleZoomOut]);

  // ==========================================
  // EFFECT: Keyboard Shortcuts (navegación + zoom)
  // ==========================================
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') goToPrev();
      if (e.key === 'ArrowRight') goToNext();
      if (e.key === 'Escape') onClose();
      
      if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        handleZoomIn();
      }
      if (e.key === '-' || e.key === '_') {
        e.preventDefault();
        handleZoomOut();
      }
      if (e.key === '0') {
        e.preventDefault();
        handleFitToScreen();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToPrev, goToNext, onClose, handleZoomIn, handleZoomOut, handleFitToScreen]);

  // ==========================================
  // EFFECT: Cleanup drag events
  // ==========================================
  

  // ==========================================
  // DISPATCHER: Acciones de galería
  // ==========================================
  const handleGalleryAction = useCallback((key, value) => {
    // Zoom selector
    if (key === 'zoom_selector' && value) {
      handleSetZoom(parseInt(value));
      return;
    }
    
    // Upload
    if (key === 'upload') {
      fileInputRef.current?.click();
      return;
    }
    
    // Delete
    if (key === 'delete') {
      handleDeleteClick();
      return;
    }
    
    // Zoom in
    if (key === 'zoom_in') {
      handleZoomIn();
      return;
    }
    
    // Zoom out
    if (key === 'zoom_out') {
      handleZoomOut();
      return;
    }
    
    // Fit to screen
    if (key === 'fit') {
      handleFitToScreen();
      return;
    }

    // View mode toggle
    if (key === 'view_mode') {
      handleViewModeToggle();
      return;
    }
    
    toast.info(`Función "${key}" en desarrollo`); /////??????????????????????????????????????
  }, [handleSetZoom, handleDeleteClick, handleZoomIn, handleZoomOut, handleFitToScreen, handleViewModeToggle]);

  // ==========================================
  // RENDER HELPERS: Iconos
  // ==========================================
  const getIcon = useCallback((iconName) => {
    const icons = {
      'upload': Upload,
      'trash': Trash2,
      'plus': Upload,
      'zoom-in': () => (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="8"/>
          <path d="m21 21-4.35-4.35"/>
          <line x1="11" y1="8" x2="11" y2="14"/>
          <line x1="8" y1="11" x2="14" y2="11"/>
        </svg>
      ),
      'zoom-out': () => (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="8"/>
          <path d="m21 21-4.35-4.35"/>
          <line x1="8" y1="11" x2="14" y2="11"/>
        </svg>
      ),
      'maximize': () => (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
        </svg>
      ),
      'layout-grid': () => (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="7" height="7"/>
          <rect x="14" y="3" width="7" height="7"/>
          <rect x="14" y="14" width="7" height="7"/>
          <rect x="3" y="14" width="7" height="7"/>
        </svg>
      ),
    };
    return icons[iconName] || Upload;
  }, []);

  const getButtonColorStyles = useCallback((color) => {
    const colorMap = {
      'blue':  btnPrimary,
      'green': btnSuccess,
      'red':   btnDanger,
      'gray':  btnSecondary
    };
    return colorMap[color] || btnPrimary;
  }, [btnPrimary, btnSuccess, btnDanger, btnSecondary]);

  // ==========================================
  // RENDER HELPERS: Archivo según tipo
  // ==========================================
  const renderFile = useCallback((file) => {
    if (!file) return null;

    const fileData = file[fileField];
    const fileType = file[typeField] || 'application/octet-stream';

    if (!fileData) {
      return (
        <div 
          className="flex flex-col items-center justify-center h-full"
          style={{
            color: galleryUI?.emptyState?.textColor || '#9ca3af'
          }}
        >
          <ImageIcon size={galleryUI?.emptyState?.iconSize || 64} />
          <p 
            className="mt-4"
            style={{
              fontSize: galleryUI?.emptyState?.fontSize || '1rem',
              fontFamily: 'inherit'
            }}
          >
            Sin archivo
          </p>
        </div>
      );
    }

    const dataUrl = `data:${fileType};base64,${fileData}`;

    if (fileType.startsWith('image/')) {
      return (
        <div
          onDoubleClick={() => handleOpenFile(file)}
          style={{
            width: '100%',
            height: '100%',
            overflow: zoomLevel > FIT_ZOOM ? 'auto' : 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <div
            style={{
              width: zoomLevel > FIT_ZOOM ? `${zoomLevel}%` : '100%',
              height: zoomLevel > FIT_ZOOM ? `${zoomLevel}%` : '100%',
              padding: zoomLevel > FIT_ZOOM ? '20px' : '0',
              boxSizing: 'border-box',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transform: zoomLevel === FIT_ZOOM ? 'none' : `scale(${zoomLevel / 100})`,
              transformOrigin: 'center center',
              transition: 'transform 0.2s ease, width 0.2s ease, height 0.2s ease',
            }}
          >
            <img 
              src={dataUrl} 
              alt="Preview"
              style={{
                display: 'block',
                maxWidth: '100%',
                maxHeight: '100%',
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                userSelect: 'none',
                pointerEvents: 'none'
              }}
              draggable={false}
            />
          </div>
        </div>
      );
    }

    if (fileType === 'application/pdf') {
      const pdfSrc = `${dataUrl}#toolbar=0&navpanes=0&scrollbar=1&zoom=${zoomLevel}`;

      return (
        <div
          style={{
            width: '100%',
            height: 'calc(100vh - 220px)',
            overflow: 'auto',
            position: 'relative',
          }}
        >
          <iframe
            key={`pdf-${currentIndex}-${zoomLevel}`}
            src={pdfSrc}
            title="PDF Preview"
            style={{
              width: '100%',
              height: '100%',
              border: 'none'
            }}
          />

          {/* ⭐ Botón flotante para abrir completo */}
          <button
            onClick={() => handleOpenFile(currentFile)}
            style={{
              position: 'absolute',
              top: '1rem',
              right: '1rem',
              height: btnBase.height || '36px', // ⭐ Altura estandarizada
              paddingLeft: btnPrimary.padding?.x || '1rem',
              paddingRight: btnPrimary.padding?.x || '1rem',
              paddingTop: btnPrimary.padding?.y || '0.5rem',
              paddingBottom: btnPrimary.padding?.y || '0.5rem',
              backgroundColor: 'transparent', // ⭐ Transparente
              color: btnSecondary.textColor || '#6b7280',
              borderRadius: btnBase.borderRadius || '6px',
              borderWidth: btnBase.borderWidth || '1px',
              borderStyle: 'solid',
              borderColor: btnSecondary.borderColor || '#d1d5db',
              fontSize: btnBase.fontSize || '0.875rem',
              fontFamily: 'inherit',
              fontWeight: btnBase.fontWeight || '500',
              cursor: 'pointer',
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              transition: 'all 0.2s ease'
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
              <polyline points="15 3 21 3 21 9"/>
              <line x1="10" y1="14" x2="21" y2="3"/>
            </svg>
            Abrir completo
          </button>
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center justify-center h-full">
        <ImageIcon 
          size={64} 
          style={{ color: galleryUI?.fileIcon?.color || '#9ca3af' }}
        />
        <p 
          className="mt-4"
          style={{
            color: galleryUI?.fileType?.textColor || '#4b5563',
            fontSize: galleryUI?.fileType?.fontSize || '0.875rem',
            fontFamily: 'inherit'
          }}
        >
          {fileType}
        </p>
        <a 
          href={dataUrl}
          download={`archivo-${currentIndex + 1}`}
          className="mt-4"
          style={{
            paddingLeft: btnPrimary.padding?.x || '1rem',
            paddingRight: btnPrimary.padding?.x || '1rem',
            paddingTop: btnPrimary.padding?.y || '0.5rem',
            paddingBottom: btnPrimary.padding?.y || '0.5rem',
            backgroundColor: btnPrimary.backgroundColor || '#3b82f6',
            color: btnPrimary.textColor || '#ffffff',
            borderRadius: btnBase.borderRadius || '6px',
            fontSize: btnBase.fontSize || '0.875rem',
            fontFamily: 'inherit',
            fontWeight: btnBase.fontWeight || '500',
            textDecoration: 'none',
            display: 'inline-block'
          }}
        >
          Descargar
        </a>
      </div>
    );
  }, [fileField, typeField, galleryUI, btnPrimary, btnBase, currentIndex, handleOpenFile, zoomLevel]);

  // ==========================================
  // RENDER: Componente principal
  // ==========================================
  const currentFile = files[currentIndex];

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center z-50 p-4"
      style={{
        backgroundColor: modales_ui?.overlay?.backgroundColor || 'rgba(0, 0, 0, 0.1)',  //fondo de pantalla
        backdropFilter: modales_ui?.overlay?.backdropBlur ? `blur(${modales_ui?.overlay?.backdropBlur})` : 'blur(4px)'
      }}
    >
      {/* Container */}
      <div
        className="w-full flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: containerConfig.backgroundColor || '#ffffff',
          borderRadius: containerConfig.borderRadius || '12px',
          boxShadow: containerConfig.boxShadow || '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          border: containerConfig.border || '1px solid rgba(0, 0, 0, 0.05)',
          maxWidth:  containerConfig.maxWidth === '4xl' ? '64rem' : compensarEscalaVh(containerConfig.maxWidth) || '64rem',
          height:    'calc(90vh / var(--ui-escala, 1))',
          minHeight: 'calc(90vh / var(--ui-escala, 1))',
          maxHeight: 'calc(90vh / var(--ui-escala, 1))',
          overflow:  containerConfig.overflow || 'hidden'
        }}
      >
        {/* Header */}
        <div 
          style={{
            paddingLeft: modalHeaderConfig.paddingX || '2rem',
            paddingRight: modalHeaderConfig.paddingX || '2rem',
            paddingTop: modalHeaderConfig.paddingY || '1.5rem',
            paddingBottom: modalHeaderConfig.paddingY || '1.5rem',
            borderBottom: modalHeaderConfig.borderBottom || '1px solid #e5e7eb',
            backgroundColor: modalHeaderConfig.backgroundColor || '#fafafa',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <div className="flex items-center" style={{ gap: modalHeaderConfig.iconGap || '1rem' }}>
            {/* Ícono */}
            <div 
              className="bg-blue-100"
              style={{
                padding: modalHeaderConfig.iconPadding || '0.625rem',
                borderRadius: modalHeaderConfig.iconBorderRadius || '10px'
              }}
            >
              <ImageIcon 
                className="text-blue-600" 
                size={parseInt(modalHeaderConfig.iconSize) || 28} 
              />
            </div>
            
            {/* Título */}
            <h2 
              className="font-semibold"
              style={{
                fontSize: modalHeaderConfig.fontSize || '1.5rem',
                fontWeight: modalHeaderConfig.fontWeight || '600',
                color: modalHeaderConfig.textColor || '#111827',
                lineHeight: modalHeaderConfig.lineHeight || '1.2',
                letterSpacing: modalHeaderConfig.letterSpacing || '-0.025em'
              }}
            >
              {title}
            </h2>
            
            {/* Counter */}
            {showCounter && files.length > 0 && (
              <span 
                style={{
                  color: controlsUI?.counter?.textColor || '#6b7280',
                  fontSize: controlsUI?.counter?.fontSize || '0.875rem',
                  fontFamily: 'inherit'
                }}
              >
                ({currentIndex + 1}/{files.length})
              </span>
            )}

            {/* Botones de ZOOM/CONTROL - Agrupados con spacing consistente */}
            <div className="flex items-center space-x-2">
              {galleryButtons
                .filter(btn => !['upload', 'delete'].includes(btn.key))
                .map(btn => {
                  // Zoom Selector
                  if (btn.type === 'selector') {
                    return (
                      <select
                        key={btn.key}
                        value={zoomLevel}
                        onChange={(e) => handleGalleryAction(btn.key, e.target.value)}
                        style={{
                          height: btnBase.height || '36px',
                          width: btn.width || '90px',
                          paddingLeft: '0.5rem',
                          paddingRight: '0.5rem',
                          fontSize: btnBase.fontSize || '0.875rem',
                          fontFamily: 'inherit',
                          borderRadius: btnBase.borderRadius || '6px',
                          borderWidth: btnBase.borderWidth || '1px',
                          borderStyle: 'solid',
                          borderColor: btnSecondary.borderColor || '#d1d5db',
                          backgroundColor: btnSecondary.backgroundColor || '#ffffff',
                          color: btnSecondary.textColor || '#374151',
                          cursor: 'pointer'
                        }}
                      >
                        {ZOOM_LEVELS.map(level => (
                          <option key={level} value={level}>
                            {level}%
                          </option>
                        ))}
                      </select>
                    );
                  }

                  // Botones normales de zoom
                  const IconComponent = getIcon(btn.icon);
                  const colorStyles = getButtonColorStyles(btn.color);
                  let isDisabled = uploading || deleting;
                  
                  return (
                    <button
                      key={btn.key}
                      onClick={() => handleGalleryAction(btn.key)}
                      disabled={isDisabled}
                      title={btn.tooltip}
                      className="flex items-center space-x-2"
                      style={{
                        height: btnBase.height || '36px',
                        paddingLeft: colorStyles.padding?.x || '1rem',
                        paddingRight: colorStyles.padding?.x || '1rem',
                        paddingTop: colorStyles.padding?.y || '0.5rem',
                        paddingBottom: colorStyles.padding?.y || '0.5rem',
                        backgroundColor: isDisabled 
                          ? (colorStyles.disabledBackgroundColor || '#9ca3af')
                          : (colorStyles.backgroundColor || '#3b82f6'),
                        color: isDisabled
                          ? (colorStyles.disabledTextColor || '#ffffff')
                          : (colorStyles.textColor || '#ffffff'),
                        borderRadius: btnBase.borderRadius || '6px',
                        borderWidth: btnBase.borderWidth || '1px',
                        borderStyle: 'solid',
                        borderColor: isDisabled
                          ? (colorStyles.disabledBorderColor || '#9ca3af')
                          : (colorStyles.borderColor || '#3b82f6'),
                        fontSize: btnBase.fontSize || '0.875rem',
                        fontFamily: 'inherit',
                        fontWeight: btnBase.fontWeight || '500',
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        transitionDuration: btnBase.transitionDuration || '150ms'
                      }}
                    >
                      <IconComponent size={16} />
                      {btn.label && <span>{btn.label}</span>}
                    </button>
                  );
                })}
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {/* Input oculto */}
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept={allowedTypes.join(',')}
              onChange={handleUpload}
              multiple
            />

            {/* Botones de ACCIÓN (upload, delete) - AL LADO DE X */}
            {galleryButtons
              .filter(btn => ['upload', 'delete'].includes(btn.key))
              .map(btn => {
                const IconComponent = getIcon(btn.icon);
                const colorStyles = getButtonColorStyles(btn.color);
                
                let isDisabled = false;
                if (btn.key === 'upload') {
                  isDisabled = uploading || deleting || files.length >= maxFiles;
                } else if (btn.key === 'delete') {
                  isDisabled = deleting || uploading || files.length === 0;
                }
                
                return (
                  <button
                    key={btn.key}
                    onClick={() => handleGalleryAction(btn.key)}
                    disabled={isDisabled}
                    title={btn.tooltip}
                    className="flex items-center space-x-2"
                    style={{
                      height: btnBase.height || '36px',
                      paddingLeft: colorStyles.padding?.x || '1rem',
                      paddingRight: colorStyles.padding?.x || '1rem',
                      paddingTop: colorStyles.padding?.y || '0.5rem',
                      paddingBottom: colorStyles.padding?.y || '0.5rem',
                      backgroundColor: isDisabled 
                        ? (colorStyles.disabledBackgroundColor || '#9ca3af')
                        : (colorStyles.backgroundColor || '#3b82f6'),
                      color: isDisabled
                        ? (colorStyles.disabledTextColor || '#ffffff')
                        : (colorStyles.textColor || '#ffffff'),
                      borderRadius: btnBase.borderRadius || '6px',
                      borderWidth: btnBase.borderWidth || '1px',
                      borderStyle: 'solid',
                      borderColor: isDisabled
                        ? (colorStyles.disabledBorderColor || '#9ca3af')
                        : (colorStyles.borderColor || '#3b82f6'),
                      fontSize: btnBase.fontSize || '0.875rem',
                      fontFamily: 'inherit',
                      fontWeight: btnBase.fontWeight || '500',
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                      transitionDuration: btnBase.transitionDuration || '150ms'
                    }}
                  >
                    {(uploading && btn.key === 'upload') || (deleting && btn.key === 'delete') ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <IconComponent size={16} />
                    )}
                    {btn.label && <span>{btn.label}</span>}
                  </button>
                );
              })}

            {/* Botón Close */}
            <button
              onClick={() => {
                if (onSuccess) onSuccess();
                onClose();
              }}
              className="transition-colors"
              style={{
                padding: closeButtonConfig.padding || '0.5rem',
                borderRadius: closeButtonConfig.borderRadius || '8px',
                color: closeButtonConfig.color || '#6b7280',
                transition: closeButtonConfig.transition || 'all 150ms ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = closeButtonConfig.hoverBackgroundColor || '#f3f4f6';
                e.target.style.color = closeButtonConfig.hoverColor || '#111827';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = closeButtonConfig.color || '#6b7280';
              }}
            >
              <X size={parseInt(closeButtonConfig.size) || 20} />
            </button>
          </div>
        </div>

        {/* Contenido */}
        <div 
          ref={imageContainerRef}
          className="flex-1"
          style={{
            padding: modalConfig.content?.padding || '1.5rem',
            minHeight: modalConfig.content?.minHeight || '500px',
            display: 'flex',
            alignItems: viewMode === 'single' ? 'center' : 'flex-start',
            justifyContent: viewMode === 'single' ? 'center' : 'flex-start',
            overflow: viewMode === 'single' ? 'hidden' : 'auto'
          }}
        >
          {loading ? (
            <Loader2 
              size={48} 
              className="animate-spin"
              style={{ color: '#3b82f6', margin: 'auto' }}
            />
          ) : files.length === 0 ? (
            <div 
              className="text-center"
              style={{
                color: galleryUI?.emptyState?.textColor || '#9ca3af',
                margin: 'auto'
              }}
            >
              <ImageIcon 
                size={galleryUI?.emptyState?.iconSize || 64}
                className="mx-auto mb-4"
              />
              <p style={{ fontSize: '1.125rem', fontFamily: 'inherit' }}>
                No hay archivos
              </p>
              <p className="mt-2" style={{ fontSize: '0.875rem', fontFamily: 'inherit' }}>
                Haz click en "Subir" para agregar
              </p>
            </div>
          ) : viewMode === 'single' ? (
            /* ========================================== */
            /* MODO SINGLE - Vista actual (carousel)     */
            /* ========================================== */
            <div className="relative w-full h-full flex items-center justify-center">
              {/* Navegación Izquierda */}
              {showNavigation && files.length > 1 && (
                <button
                  onClick={goToPrev}
                  className="absolute rounded-full"
                  style={{
                    left: controlsUI?.navigation?.offset || '1rem',
                    padding: controlsUI?.navigation?.padding || '0.75rem',
                    backgroundColor: controlsUI?.navigation?.backgroundColor || 'rgba(107, 114, 128, 0.75)',
                    color: controlsUI?.navigation?.textColor || '#ffffff',
                    transitionDuration: '150ms',
                    zIndex: 10
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = controlsUI?.navigation?.hoverBackgroundColor || 'rgba(107, 114, 128, 1)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = controlsUI?.navigation?.backgroundColor || 'rgba(107, 114, 128, 0.75)';
                  }}
                >
                  <ChevronLeft size={controlsUI?.navigation?.iconSize || 32} />
                </button>
              )}

              {/* Preview */}
              <div 
                style={{
                  maxWidth: '100%', //galleryUI?.preview?.maxWidth || '80rem',
                  maxHeight: '100%',
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {renderFile(currentFile)}
              </div>

              {/* Navegación Derecha */}
              {showNavigation && files.length > 1 && (
                <button
                  onClick={goToNext}
                  className="absolute rounded-full"
                  style={{
                    right: controlsUI?.navigation?.offset || '1rem',
                    padding: controlsUI?.navigation?.padding || '0.75rem',
                    backgroundColor: controlsUI?.navigation?.backgroundColor || 'rgba(107, 114, 128, 0.75)',
                    color: controlsUI?.navigation?.textColor || '#ffffff',
                    transitionDuration: '150ms',
                    zIndex: 10
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = controlsUI?.navigation?.hoverBackgroundColor || 'rgba(107, 114, 128, 1)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = controlsUI?.navigation?.backgroundColor || 'rgba(107, 114, 128, 0.75)';
                  }}
                >
                  <ChevronRight size={controlsUI?.navigation?.iconSize || 32} />
                </button>
              )}
            </div>
          ) : (
            /* ========================================== */
            /* MODO GRID - Vista mosaico profesional     */
            /* ========================================== */
            <div 
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: '1rem',
                width: '100%',
                padding: '0.5rem'
              }}
            >
              {files.map((file, index) => {
                const fileData = file[fileField];
                const fileType = file[typeField] || 'application/octet-stream';
                const dataUrl = fileData ? `data:${fileType};base64,${fileData}` : null;

                return (
                  <div
                    key={index}
                    onClick={() => {
                      setViewMode('single');
                      setCurrentIndex(index);
                    }}
                    style={{
                      position: 'relative',
                      aspectRatio: '1',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      backgroundColor: '#f3f4f6',
                      transition: 'transform 150ms ease, box-shadow 150ms ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.05)';
                      e.currentTarget.style.boxShadow = '0 10px 25px -5px rgba(0, 0, 0, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    {dataUrl && fileType.startsWith('image/') ? (
                      <img 
                        src={dataUrl} 
                        alt={`Imagen ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    ) : dataUrl && fileType === 'application/pdf' ? (
                      <div 
                        style={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexDirection: 'column',
                          backgroundColor: '#ef4444',
                          color: '#ffffff',
                          position: 'relative'
                        }}
                      >
                        {/* Ícono PDF estilizado */}
                        <svg 
                          width="64" 
                          height="64" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2"
                          style={{ marginBottom: '0.5rem' }}
                        >
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                          <polyline points="14 2 14 8 20 8"/>
                          <path d="M10 12h4"/>
                          <path d="M10 16h4"/>
                        </svg>
                        <p style={{ fontSize: '1rem', fontWeight: '600', margin: 0 }}>
                          PDF
                        </p>
                        <p style={{ fontSize: '0.65rem', marginTop: '0.25rem', opacity: 0.9 }}>
                          Click para ver
                        </p>
                      </div>
                    ) : (
                      <div 
                        style={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexDirection: 'column',
                          color: '#9ca3af'
                        }}
                      >
                        <ImageIcon size={48} />
                        <p style={{ fontSize: '0.75rem', marginTop: '0.5rem' }}>
                          {fileType.split('/')[1]?.toUpperCase() || 'FILE'}
                        </p>
                      </div>
                    )}
                    
                    {/* Overlay con info */}
                    <div
                      style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        padding: '0.5rem',
                        background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
                        color: '#ffffff',
                        fontSize: '0.75rem',
                        opacity: 0,
                        transition: 'opacity 150ms ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = 1;
                      }}
                    >
                      <p style={{ margin: 0, fontWeight: '500' }}>
                        Imagen {index + 1}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer - Thumbnails */}
        {showThumbnails && files.length > 1 && (
          <div 
            className="flex justify-center space-x-2 overflow-x-auto"
            style={{
              paddingLeft: footerConfig.paddingX || '2rem',
              paddingRight: footerConfig.paddingX || '2rem',
              paddingTop: footerConfig.paddingY || '1.25rem',
              paddingBottom: footerConfig.paddingBottom || footerConfig.paddingY || '1.25rem',
              borderTop: '1px solid #e5e7eb',
              backgroundColor: footerConfig.backgroundColor || '#fafafa'
            }}
          >
            {files.map((file, index) => {
              const fileData = file[fileField];
              const fileType = file[typeField] || 'application/octet-stream';
              const dataUrl = fileData ? `data:${fileType};base64,${fileData}` : null;

              return (
                <button
                  key={index}
                  onClick={() => goToIndex(index)}
                  className="flex-shrink-0 rounded overflow-hidden"
                  style={{
                    width: thumbnailsUI?.size || '5rem',
                    height: thumbnailsUI?.size || '5rem',
                    borderWidth: '2px',
                    borderStyle: 'solid',
                    borderColor: index === currentIndex ? '#3b82f6' : '#d1d5db',
                    opacity: index === currentIndex ? 1 : 0.6,
                    transitionDuration: '150ms'
                  }}
                  onMouseEnter={(e) => {
                    if (index !== currentIndex) {
                      e.target.style.opacity = 1;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (index !== currentIndex) {
                      e.target.style.opacity = 0.6;
                    }
                  }}
                >
                  {dataUrl && fileType.startsWith('image/') ? (
                    <img 
                      src={dataUrl} 
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div 
                      className="w-full h-full flex items-center justify-center"
                      style={{
                        backgroundColor: '#f3f4f6'
                      }}
                    >
                      <ImageIcon 
                        size={24} 
                        style={{ color: '#9ca3af' }}
                      />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
      {/* Modal: Confirmar Eliminar imagen */}
      {pendingDelete && (
        schema?.confirm_simple === true ? (
          <SqlexecConfirmDialog
            key="confirm-delete-img-simple"
            isOpen={true}
            tipo="danger"
            pregunta={`¿Deseas eliminar ${schema?.title || 'esta imagen'}?`}
            onClose={() => setPendingDelete(false)}
            onConfirm={() => { setPendingDelete(false); handleConfirmDelete(); }}
          />
        ) : (
          <SqlexecMasterDetail
            key="confirm-delete-img"
            componentForm="Sys_Confirma_Mensaje"
            mode="create"
            useGenerico={true}
            initialData={{
              idconfirma: 1,
              mode:   'delete_catalogo',
              titulo: schema?.title || 'Imagen',
            }}
            onClose={() => setPendingDelete(false)}
            onSuccess={() => { setPendingDelete(false); handleConfirmDelete(); }}
          />
        )
      )}
    </div>  
  );
};

export default FileGalleryManager;